import { useState, useEffect } from 'react'
import { useApi } from '../../../shared/hooks/useApi'
import { formatDate, getNoticeTypeLabel, formatCurrency } from '../../../shared/utils/formatters'
import {
  Calendar,
  FileText,
  CheckCircle,
  Clock,
  AlertCircle,
  ArrowRight,
  ExternalLink,
} from 'lucide-react'

interface TimelineNotice {
  id: string
  notice_type: string
  order_object?: string
  publication_date: string
  total_contract_value?: number
  procedure_result_label?: string
  notice_number?: string
  is_current_notice: boolean
}

interface TimelineData {
  tender_id?: string
  lifecycle_stage: string
  notices: TimelineNotice[]
}

interface TenderTimelineProps {
  noticeId: string
  onNoticeClick?: (noticeId: string) => void
}

function getNoticeTypeIcon(noticeType: string) {
  switch (noticeType) {
    case 'ContractNotice':
      return FileText
    case 'TenderResultNotice':
      return CheckCircle
    case 'ContractPerformingNotice':
      return CheckCircle
    case 'NoticeUpdateNotice':
      return Clock
    default:
      return FileText
  }
}

function getNoticeTypeColor(noticeType: string, isCurrent = false) {
  if (isCurrent) {
    return 'bg-brand text-white border-brand'
  }

  switch (noticeType) {
    case 'ContractNotice':
      return 'bg-brand-bg text-brand border-brand'
    case 'NoticeUpdateNotice':
      return 'bg-warning/10 text-warning border-warning/20'
    case 'TenderResultNotice':
      return 'bg-success/10 text-success border-success/20'
    case 'ContractPerformingNotice':
      return 'bg-ai/10 text-ai border-ai/10'
    default:
      return 'bg-surface-inset text-ink border-border'
  }
}

function getLifecycleStageInfo(stage: string) {
  switch (stage) {
    case 'planning':
      return {
        label: 'Planowanie',
        description: 'Ogłoszenie o zamówieniu i ewentualne aktualizacje',
        color: 'text-brand',
        bgColor: 'bg-brand-bg',
      }
    case 'tendering':
      return {
        label: 'Przetarg',
        description: 'Aktywne zbieranie ofert od wykonawców',
        color: 'text-warning',
        bgColor: 'bg-warning/5',
      }
    case 'awarding':
      return {
        label: 'Rozstrzyganie',
        description: 'Ocena ofert i wybór wykonawcy',
        color: 'text-success',
        bgColor: 'bg-success/5',
      }
    case 'performing':
      return {
        label: 'Realizacja',
        description: 'Wykonywanie umowy i nadzór nad realizacją',
        color: 'text-ai',
        bgColor: 'bg-ai/5',
      }
    default:
      return {
        label: 'Nieznany etap',
        description: 'Status postępowania nieokreślony',
        color: 'text-ink-secondary',
        bgColor: 'bg-surface',
      }
  }
}

const TenderTimeline = ({ noticeId, onNoticeClick }: TenderTimelineProps) => {
  const { getRelatedNotices, loading, error } = useApi()
  const [timelineData, setTimelineData] = useState<TimelineData | null>(null)

  useEffect(() => {
    const loadTimeline = async () => {
      if (!noticeId) return

      try {
        const data = await getRelatedNotices(noticeId)
        setTimelineData(data)
      } catch (err) {
        console.error('Failed to load tender timeline:', err)
      }
    }

    loadTimeline()
  }, [noticeId, getRelatedNotices])

  if (loading) {
    return (
      <div className="bg-surface-raised border border-border rounded-lg p-6">
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-surface-raised border border-border rounded-lg p-6">
        <div className="text-center text-ink-muted">
          <AlertCircle className="h-8 w-8 mx-auto mb-2 text-ink-faint" />
          <p>Nie udało się załadować osi czasu postępowania</p>
          <p className="text-sm text-ink-faint mt-1">{error}</p>
        </div>
      </div>
    )
  }

  if (!timelineData || timelineData.notices.length <= 1) {
    return (
      <div className="bg-surface-raised border border-border rounded-lg p-6">
        <div className="text-center text-ink-muted">
          <FileText className="h-8 w-8 mx-auto mb-2 text-ink-faint" />
          <p>Brak powiązanych ogłoszeń w tym postępowaniu</p>
          <p className="text-sm text-ink-faint mt-1">
            To ogłoszenie nie ma powiązanych dokumentów w cyklu życia przetargu
          </p>
        </div>
      </div>
    )
  }

  const stageInfo = getLifecycleStageInfo(timelineData.lifecycle_stage)

  return (
    <div className="bg-surface-raised border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-ink flex items-center">
              <Calendar className="h-5 w-5 mr-2 text-ink-muted" />
              Oś czasu postępowania
            </h3>
            <p className="text-sm text-ink-secondary mt-1">
              ID przetargu:{' '}
              <code className="bg-surface-inset px-2 py-1 rounded text-xs font-mono">
                {timelineData.tender_id}
              </code>
            </p>
          </div>
          <div
            className={`px-3 py-1 rounded-full text-sm font-medium ${stageInfo.bgColor} ${stageInfo.color}`}
          >
            {stageInfo.label}
          </div>
        </div>
        <p className="text-sm text-ink-muted mt-2">{stageInfo.description}</p>
      </div>

      {/* Timeline */}
      <div className="p-6">
        <div className="space-y-4">
          {timelineData.notices.map((notice, index) => {
            const IconComponent = getNoticeTypeIcon(notice.notice_type)
            const isLast = index === timelineData.notices.length - 1
            const isFirst = index === 0

            return (
              <div key={notice.id} className="relative">
                {/* Timeline line */}
                {!isLast && <div className="absolute left-6 top-12 w-0.5 h-16 bg-surface-inset"></div>}

                <div className="flex items-start space-x-4">
                  {/* Icon */}
                  <div
                    className={`flex-shrink-0 w-12 h-12 rounded-full border-2 flex items-center justify-center ${getNoticeTypeColor(notice.notice_type, notice.is_current_notice)}`}
                  >
                    <IconComponent className="h-5 w-5" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <h4 className="text-sm font-medium text-ink">
                          {getNoticeTypeLabel(notice.notice_type)}
                        </h4>
                        {notice.is_current_notice && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-brand-bg text-brand">
                            Aktualne ogłoszenie
                          </span>
                        )}
                      </div>
                      {onNoticeClick && (
                        <button
                          onClick={() => onNoticeClick(notice.id)}
                          className="text-brand hover:text-brand p-1 rounded hover:bg-brand-bg transition-colors"
                          title="Przejdź do ogłoszenia"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </button>
                      )}
                    </div>

                    <div className="mt-1 text-sm text-ink-secondary">
                      {notice.order_object && <p className="line-clamp-2">{notice.order_object}</p>}
                    </div>

                    <div className="mt-2 flex items-center space-x-4 text-xs text-ink-muted">
                      {notice.publication_date && (
                        <div className="flex items-center">
                          <Calendar className="h-3 w-3 mr-1" />
                          {formatDate(notice.publication_date)}
                        </div>
                      )}
                      {notice.total_contract_value && (
                        <div className="flex items-center">
                          <span className="font-medium text-ink-secondary">
                            {formatCurrency(notice.total_contract_value, 'PLN')}
                          </span>
                        </div>
                      )}
                      {notice.notice_number && <div>Nr: {notice.notice_number}</div>}
                    </div>

                    {notice.procedure_result_label && (
                      <div className="mt-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-surface-inset text-ink">
                          {notice.procedure_result_label}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Arrow between items */}
                {!isLast && (
                  <div className="flex justify-start mt-4 mb-4">
                    <div className="flex items-center text-ink-faint">
                      <ArrowRight className="h-4 w-4 text-ink-faint" />
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 py-3 bg-surface border-t border-border">
        <div className="text-xs text-ink-muted">
          Łącznie {timelineData.notices.length} powiązanych ogłoszeń w tym postępowaniu
        </div>
      </div>
    </div>
  )
}

export default TenderTimeline
